import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useTranslation, Trans } from "react-i18next";

import qs from "qs";
import color from "color";
import { styled } from "@mui/material/styles";

import Typography from "@mui/material/Typography";

import {
  Routes,
  Route,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";

import { motion } from "framer-motion";

import { client } from "../../bit/board/hooks/useGraphQLClient";
// import usePageTracking from "../../bit/tracking/usePageTracking";
// import useEventTracking from "../../bit/tracking/useEventTracking";

import { QUERY_ACCESS_EVENT } from "../../config/graphql/query";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import Curve from "../../components/Animations/PageTransitions/Curve";

import Message from "./Message";
import Calling from "../Calling";
import Bump from "../../components/Animations/Bump";
import TeamsMessage from "./TeamsMessage";
import GoogleChatMessage from "./GoogleChatMessage";

import {
  useTeams,
  useTeamsVideoCallEnabled,
  useGoogleChat,
} from "../../lib/hooks/useQueryBoard";
import { MUTATION_ACCESS_EVENT_GET_CALLER_ACS_TOKEN_AND_TARGET_TEAMS_USER_ID } from "../../config/graphql/mutation";
import TeamsAudioCall from "../Teams/TeamsAudioCall";

export const Status = {
  Connecting: "connecting",
  Ringing: "ringing",
  Open: "open",
  Error: "error",
  Close: "close",
};

const ContactButton = styled(motion.button)(({ theme }) => ({
  margin: "2.5rem",
  minWidth: "16.4rem",
  padding: "1.5rem",
  borderRadius: "1rem",
  background: theme.palette.primary.main,

  h3: {
    color: theme.palette.primary.contrastText,
  },

  "&:disabled": {
    backgroundColor: color(theme.palette.primary.main).lighten(0.4).hex(),
  },
}));

type NamespaceType = keyof Required<IMemberEmployeePreferences>;

function getPreference(
  receiver: IMemberEmployee | undefined,
  namespace: NamespaceType | undefined,
  action: keyof Required<IPreferenceChannels>,
): boolean {
  if (!receiver) {
    return false;
  }

  if (!namespace) {
    return false;
  }

  const preference = receiver?.availablePreferences?.[namespace];

  return !!(preference?.enabled && preference?.channel?.[action]);
}

function getAutoPreference(
  receiver: IMemberEmployee | undefined,
  namespace: NamespaceType | undefined,
  action: keyof Required<IPreferenceChannels>,
): boolean {
  if (!receiver) {
    return false;
  }

  if (!namespace) {
    return false;
  }

  const preference = receiver?.availablePreferences?.[namespace];

  return !!(preference?.enabled && preference?.autoChannel?.[action]);
}

const ContactPage = () => {
  const { t } = useTranslation(["call"]);

  const history = useNavigate();

  const { id } = useParams<{ id: string }>();

  const { pathname } = useLocation();

  const teams = useTeams();
  const teamsVideoCallEnabled = useTeamsVideoCallEnabled();
  const googleChat = useGoogleChat();

  const mainRoute = new RegExp(`^\/contact\/${id}\/?$`).test(pathname);

  const { data: accessEventData } = useQuery(
    [`access/event/${id}`],
    () =>
      client.request<{ accessEvent: IAccessEvent }>(QUERY_ACCESS_EVENT, { id }),
    {
      retry: false,
      staleTime: 1 * (60 * 1000),
      onError: () => {
        history("/", { replace: true });
      },
    },
  );

  const namespace = useMemo((): NamespaceType | undefined => {
    switch (accessEventData?.accessEvent?.type) {
      case "Food": {
        return "foodDelivery";
      }

      case "Package": {
        return "packageDelivery";
      }

      case "Guest":
      case "Visitor": {
        return "visitor";
      }

      case "MeetingAttendee": {
        return "meetingAttendee";
      }

      default: {
        return undefined;
      }
    }
  }, [accessEventData]);

  const receiver = accessEventData?.accessEvent?.receiver;

  const autoEmail = useMemo(
    () => getAutoPreference(receiver, namespace, "email"),
    [accessEventData],
  );
  const autoSMS = useMemo(
    () => getAutoPreference(receiver, namespace, "sms"),
    [accessEventData],
  );

  const availableForCall = useMemo(
    () => getPreference(receiver, namespace, "call"),
    [accessEventData],
  );

  const availableForPing = useMemo(
    () => getPreference(receiver, namespace, "ping"),
    [accessEventData],
  );

  const availableForSMS = useMemo(
    () => getPreference(receiver, namespace, "sms"),
    [accessEventData],
  );

  const availableForVideoCall = useMemo(
    () => getPreference(receiver, namespace, "videoCall"),
    [accessEventData],
  );

  const availableForTeamsCall = useMemo(
    () => getPreference(receiver, namespace, "teamsCall"),
    [accessEventData],
  );

  const availableForTeamsMessage = useMemo(
    () => getPreference(receiver, namespace, "teamsMessage"),
    [accessEventData],
  );

  const availableForGoogleChatMessage = useMemo(
    () => getPreference(receiver, namespace, "googleChatMessage"),
    [accessEventData],
  );

  const { data: CallerAcsTokenAndTargetTeamsUserId, status: teamsTokenStatus } =
    useQuery(
      [`access/event/${id}/teams`],
      () => {
        return client.request(
          MUTATION_ACCESS_EVENT_GET_CALLER_ACS_TOKEN_AND_TARGET_TEAMS_USER_ID,
          {
            id,
          },
        );
      },
      {
        enabled: availableForTeamsCall,
        retry: false,
        refetchOnWindowFocus: false,
      },
    );

  const callerAcsToken =
    CallerAcsTokenAndTargetTeamsUserId
      ?.accessEventGetCallerAcsTokenAndTargetTeamsUserId?.callerAcsToken;
  const targetTeamsUserId =
    CallerAcsTokenAndTargetTeamsUserId
      ?.accessEventGetCallerAcsTokenAndTargetTeamsUserId?.targetTeamsUserId;

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to="/" />
        <Title disableAnimation={!mainRoute}>
          <Typography variant="h1" className="mt-3">
            <Trans
              i18nKey={`call:title.${
                autoEmail || autoSMS ? "email-sms" : "no-email-sms"
              }`}
            />
          </Typography>
        </Title>
        <div className="mt-5 d-flex flex-wrap justify-content-center">
          {receiver?.phone && availableForCall && (
            <Bump disabled={!mainRoute}>
              <ContactButton
                onClick={() => {
                  history({
                    pathname: `call`,
                  });
                }}
              >
                <Typography variant="h3">{t("call:callBtn")}</Typography>
              </ContactButton>
            </Bump>
          )}
          {receiver?.phone && availableForPing && (
            <Bump disabled={!mainRoute}>
              <ContactButton
                onClick={() => {
                  history({
                    pathname: `ping`,
                  });
                }}
              >
                <Typography variant="h3">{t("call:pingBtn")}</Typography>
              </ContactButton>
            </Bump>
          )}
          {receiver?.phone && availableForSMS && (
            <Bump disabled={!mainRoute}>
              <ContactButton
                onClick={() => {
                  history({
                    pathname: `message`,
                    search: `?${qs.stringify({
                      type: accessEventData?.accessEvent?.type,
                    })}`,
                  });
                }}
              >
                <Typography variant="h3">{t("call:smsBtn")}</Typography>
              </ContactButton>
            </Bump>
          )}

          {availableForVideoCall && (
            <Bump disabled={!mainRoute}>
              <ContactButton
                onClick={() => {
                  return history(
                    `/video-call?${qs.stringify({
                      id,
                      type: accessEventData?.accessEvent?.type,
                    })}`,
                  );
                }}
              >
                <Typography variant="h3">{t("call:callVideoBtn")}</Typography>
              </ContactButton>
            </Bump>
          )}

          {!!teams && (
            <>
              {availableForTeamsCall && (
                <Bump disabled={!mainRoute}>
                  <ContactButton
                    disabled={teamsTokenStatus !== "success"}
                    onClick={() => {
                      if (teamsVideoCallEnabled) {
                        return history(
                          `/teams-video-call?${qs.stringify({
                            callerAcsToken,
                            targetTeamsUserIds: targetTeamsUserId,
                            type: accessEventData?.accessEvent?.type,
                          })}`,
                          {
                            replace: true,
                          },
                        );
                      }

                      const searchParams = qs.stringify({
                        callerAcsToken,
                        targetTeamsUserIds: targetTeamsUserId,
                        type: accessEventData?.accessEvent?.type,
                      });

                      return history({
                        pathname: "teams-audio-call",
                        search: `?${searchParams}`,
                      });
                    }}
                  >
                    <Typography variant="h3">
                      {t("call:teamsCallBtn")}
                    </Typography>
                  </ContactButton>
                </Bump>
              )}
              {availableForTeamsMessage && (
                <Bump disabled={!mainRoute}>
                  <ContactButton
                    onClick={() => {
                      history({
                        pathname: "teamsMessage",
                        search: `?${qs.stringify({
                          type: accessEventData?.accessEvent?.type,
                        })}`,
                      });
                    }}
                  >
                    <Typography variant="h3">
                      {t("call:teamsMessageBtn")}
                    </Typography>
                  </ContactButton>
                </Bump>
              )}
            </>
          )}
          {!!googleChat && availableForGoogleChatMessage && (
            <Bump disabled={!mainRoute}>
              <ContactButton
                onClick={() => {
                  history({
                    pathname: "googleChatMessage",
                    search: `?${qs.stringify({
                      type: accessEventData?.accessEvent?.type,
                    })}`,
                  });
                }}
              >
                <Typography variant="h3">
                  {t("call:googleChatMessageBtn")}
                </Typography>
              </ContactButton>
            </Bump>
          )}
        </div>
      </div>

      <Routes>
        {!!receiver?.phone && (
          <>
            <Route path="call" element={<Calling />} />
            <Route path="ping" element={<Calling />} />
            <Route path="message" element={<Message />} />
          </>
        )}
        <Route path="teams-audio-call" element={<TeamsAudioCall />} />
        <Route path="teamsMessage" element={<TeamsMessage />} />
        <Route path="googleChatMessage" element={<GoogleChatMessage />} />
      </Routes>
    </Curve>
  );
};

export default ContactPage;
